/**
 * AI Performance Dashboard
 * Redesigned to follow shadcn/ui design rules:
 *   • CardHeader CSS-grid with CardAction (refresh button)
 *   • Section labels: xs uppercase tracking-wider muted
 *   • Stat tiles: label+icon row / value pattern (section-cards style)
 *   • Progress bars: colour dot + tabular counts
 *   • Business hours: outline Badge with semantic colour classes (no custom variants)
 *   • Escalation: divide-x grid (no manual Separator)
 *   • No decorative Separators between sections — spacing creates hierarchy
 *
 * All API calls, data shapes, and business logic are unchanged.
 */

import React, { useState, useEffect } from 'react';
import {
  Bot,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Gauge,
  Timer,
} from 'lucide-react';
import api from '../../services/api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';

interface AIMetrics {
  total_conversations: number;
  ai_conversations: number;
  human_conversations: number;
  escalated_conversations: number;
  average_confidence: number;
  average_response_time_ms: number;
  business_hours_active: boolean;
  business_hours_status?: string;
  pending_escalations: number;
  resolved_escalations: number;
  ai_success_rate: number;
}

export const AIDashboard: React.FC = () => {
  const [metrics, setMetrics]     = useState<AIMetrics | null>(null);
  const [loading, setLoading]     = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/ai-metrics');
      setMetrics(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load AI metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Skeleton ─────────────────────────────────────────────────────────────────
  if (!metrics && loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><Skeleton className="h-5 w-52" /></CardTitle>
          <CardDescription><Skeleton className="h-3.5 w-40 mt-0.5" /></CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-1.5 w-full rounded-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-1.5 w-full rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-[72px] rounded-lg" />)}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Skeleton className="h-14 rounded-lg" />
            <Skeleton className="h-14 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (!metrics) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Failed to load AI metrics</p>
        </CardContent>
      </Card>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────────
  const aiPercentage    = metrics.total_conversations > 0
    ? (metrics.ai_conversations    / metrics.total_conversations) * 100 : 0;
  const humanPercentage = metrics.total_conversations > 0
    ? (metrics.human_conversations / metrics.total_conversations) * 100 : 0;

  // Stat tiles — label+icon row above value (section-cards inspired)
  const statTiles = [
    { icon: Gauge,         value: `${(metrics.average_confidence * 100).toFixed(0)}%`, label: 'Avg Confidence' },
    { icon: Timer,         value: `${(metrics.average_response_time_ms / 1000).toFixed(1)}s`, label: 'Avg Response' },
    { icon: AlertTriangle, value: String(metrics.pending_escalations), label: 'Pending Escalations' },
    { icon: CheckCircle2,  value: `${(metrics.ai_success_rate * 100).toFixed(0)}%`, label: 'AI Success Rate' },
  ] as const;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Card>

      {/* ── CardHeader: CSS-grid — title + description / refresh (CardAction) ── */}
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Bot className="h-4 w-4 text-primary" />
          AI Performance Dashboard
        </CardTitle>
        <CardDescription>
          Realtime · Updated {lastUpdate.toLocaleTimeString()}
        </CardDescription>
        <CardAction>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={loadMetrics}
            disabled={loading}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-6">

        {/* ── Section 1: Active Conversation Distribution ────────────────── */}
        <div className="space-y-3">
          {/* Section label row */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Active Conversations
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {metrics.total_conversations} total
            </span>
          </div>

          {/* AI bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-primary" />
                <span className="font-medium">AI Handling</span>
              </div>
              <span className="tabular-nums text-muted-foreground">
                {metrics.ai_conversations}
                <span className="ml-1.5 text-muted-foreground/60">
                  ({aiPercentage.toFixed(0)}%)
                </span>
              </span>
            </div>
            <Progress value={aiPercentage} className="h-1.5" />
          </div>

          {/* Human bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />
                <span className="font-medium text-muted-foreground">Human Handling</span>
              </div>
              <span className="tabular-nums text-muted-foreground">
                {metrics.human_conversations}
                <span className="ml-1.5 text-muted-foreground/60">
                  ({humanPercentage.toFixed(0)}%)
                </span>
              </span>
            </div>
            <Progress
              value={humanPercentage}
              className="h-1.5 [&>div]:bg-muted-foreground/40"
            />
          </div>
        </div>

        {/* ── Section 2: Key Metric Tiles (section-cards style) ─────────── */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {statTiles.map(({ icon: Icon, value, label }) => (
            <div
              key={label}
              className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3"
            >
              {/* Label + icon row */}
              <div className="flex items-center justify-between">
                <span className="text-xs leading-none text-muted-foreground">{label}</span>
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              {/* Value */}
              <span className="text-xl font-semibold tabular-nums">{value}</span>
            </div>
          ))}
        </div>

        {/* ── Section 3: Business Hours + Escalation ────────────────────── */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

          {/* Business Hours status */}
          <div className="flex flex-col justify-center gap-1 rounded-lg border border-border bg-muted/30 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-medium">Business Hours</span>
              </div>
              <Badge
                variant="outline"
                className={cn(
                  'gap-1.5 font-semibold',
                  metrics.business_hours_active
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                    : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
                )}
              >
                <span
                  className={cn(
                    'inline-block h-1.5 w-1.5 rounded-full',
                    metrics.business_hours_active ? 'bg-emerald-500' : 'bg-amber-500',
                  )}
                />
                {metrics.business_hours_active ? 'Active' : 'Outside Hours'}
              </Badge>
            </div>
            {metrics.business_hours_status && (
              <p className="text-xs text-muted-foreground/70 pl-[22px]">
                {metrics.business_hours_status}
              </p>
            )}
          </div>

          {/* Escalation summary — divide-x separates the 3 counters */}
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
            <p className="mb-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Escalation Summary
            </p>
            <div className="grid grid-cols-3 divide-x divide-border">
              <div className="pr-3 text-center">
                <p className="text-xl font-semibold tabular-nums">
                  {metrics.escalated_conversations}
                </p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="px-3 text-center">
                <p className="text-xl font-semibold tabular-nums text-destructive">
                  {metrics.pending_escalations}
                </p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="pl-3 text-center">
                <p className="text-xl font-semibold tabular-nums">
                  {metrics.resolved_escalations}
                </p>
                <p className="text-xs text-muted-foreground">Resolved</p>
              </div>
            </div>
          </div>

        </div>

      </CardContent>
    </Card>
  );
};

export default AIDashboard;
